import { observer } from 'mobx-react';
import * as React from 'react';
import { ActiveProgram } from '../../../../ProgramStore/Types';
import * as style from '../../../ProgramSummary/style.module.css';
import { TranslationsState, TranslationsContext } from '@execonline-inc/translations';
import { assertNever } from '@kofno/piper';
import { warnAndNotify } from '../../../../Honeybadger';
import ScheduledRun from './ScheduledRun';

interface Props {
  programDetail: ActiveProgram;
}

const numberFormatter = (language: string, options: Intl.NumberFormatOptions) =>
  new Intl.NumberFormat(language, options).format;

const formatPercentage = (language: string, value: number): string => {
  const roundedValue = Math.floor(value * 100) / 100;
  return numberFormatter(language, {
    style: 'percent',
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })(roundedValue / 100);
};

export const localization = (language: string, percentComplete: number): string =>
  formatPercentage(language, percentComplete);

export const localizer = (percentComplete: number) => (ts: TranslationsState) => {
  switch (ts.kind) {
    case 'loaded-from-fallback':
    case 'loaded':
      return localization(ts.language, percentComplete);
    case 'uninitialized':
      warnAndNotify('currencyLocalizationError', 'localization is uninitialized', percentComplete);
      return String(percentComplete);
    default:
      assertNever(ts);
  }
};

const programInstanceRender = (programDetail: ActiveProgram) => (ts: TranslationsState) => {
  const { programInstanceKind, percentComplete } = programDetail;

  switch (programInstanceKind) {
    case 'on-demand':
      return (
        <span
          className={style.percentage}
          data-testid="percent-complete-on-demand"
          tabIndex={0}
          aria-label={`Progress: ${localizer(percentComplete)(ts)}`}
        >
          {localizer(percentComplete)(ts)}
        </span>
      );
    case 'scheduled':
      return <ScheduledRun programDetail={programDetail} translationsState={ts} />;
  }
};

const Percentage: React.FC<Props> = ({ programDetail }) => (
  <TranslationsContext.Consumer>
    {(ts) => programInstanceRender(programDetail)(ts)}
  </TranslationsContext.Consumer>
);

export default observer(Percentage);
